import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form, useFormikContext } from 'formik';
import { useTranslate } from 'react-translate.ts';
import { Button, ButtonVariants } from '@tcc-online/common-components';
import { ObjectSchema, StringSchema } from 'yup';

import { SIGN_IN_PATH } from '../../../../constants/route.constants';
import {
    TRANSLATIONS_EMAIL_FIELD_KEY,
    TRANSLATIONS_REQUIRED_FIELD_KEY,
    TRANSLATIONS_ROOT_KEY,
} from '../../../../constants/translation.constants';
import { FormInput } from '@tcc-online/input-components';
import AuthControls from '../../AuthControls/AuthControls';
import AuthForm from '../../AuthForm/AuthForm';

const EMAIL_FIELD = 'email';

type ForgottenPasswordFormProps = {
    onSubmit: (email: string) => void;
    error: boolean;
};

type Values = {
    [EMAIL_FIELD]: string;
};

export function ForgottenPasswordForm(props: ForgottenPasswordFormProps) {
    const { onSubmit, error } = props;
    const translate = useTranslate();

    return (
        <Formik<Values>
            initialValues={{ email: '' }}
            onSubmit={submitForm}
            validationSchema={getValidationSchema}
        >
            <InnerForm error={error} />
        </Formik>
    );

    function getValidationSchema() {
        return new ObjectSchema({
            [EMAIL_FIELD]: new StringSchema()
                .required(translate(TRANSLATIONS_REQUIRED_FIELD_KEY))
                .email(translate(TRANSLATIONS_EMAIL_FIELD_KEY)),
        });
    }

    function submitForm(values: Values) {
        onSubmit(values.email);
    }
}

function InnerForm(props: { error: boolean }) {
    const { error } = props;
    const translate = useTranslate(TRANSLATIONS_ROOT_KEY);

    const formik = useFormikContext();
    const formikRef = useRef(formik);
    const translateRef = useRef(translate);

    useEffect(() => {
        formikRef.current.setErrors({
            [EMAIL_FIELD]: error
                ? translateRef.current('forgotten-password.error')
                : null,
        });
    }, [error]);

    return (
        <Form>
            <AuthForm>
                <FormInput
                    type="email"
                    name={EMAIL_FIELD}
                    label={translate('login.email')}
                    // inputProps={{ "data-test": "email" }}
                />
            </AuthForm>
            <AuthControls>
                <Link to={SIGN_IN_PATH}>
                    <div className="font-heavy">
                        {translate('login.company.back')}
                    </div>
                </Link>
                <Button type="submit" variant={ButtonVariants.positive}>
                    {translate('forgotten-password.send')}
                </Button>
            </AuthControls>
        </Form>
    );
}
